import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Form, Spinner, Alert, Toast, ToastContainer } from 'react-bootstrap';
import { dashboardService, GlobalStats, OpsAlertBucket, OpsAlertSummary, UserStatsResponse, User, Transaction } from '../../services/dashboardService';
import { APP_NOTIFICATIONS_CHANGED_EVENT, AppNotification, appNotificationService } from '../../services/appNotificationService';
import { getCurrentUserRole, getCurrentUserId } from '../../utils/auth';
import { getRoleDisplayLabel, SUB_MERCHANT_LABEL } from '../../utils/displayLabels';
import { resolveSafeOperatorRoute } from '../../utils/internalRouteGuard';
import InternalPageHeader from '../Layout/InternalPageHeader';
import { sortByNewest } from '../../utils/sortByNewest';
import {
  browserAlertsSupported,
  getBrowserAlertPermission,
  getPaymentToneEnabled,
  playPaymentTone,
  requestBrowserAlertPermission,
  sendBrowserAlert,
  setPaymentToneEnabled,
} from '../../utils/browserAlerts';

const RECEIVED_SETTLING_STATUS = 'Payment received (Settling)';
const DASHBOARD_POLL_INTERVAL_MS = 15000;

const isPaymentReceivedState = (status?: string | null, displayStatus?: string | null) =>
  status === 'Completed' || status === 'PayoutCompleted' || displayStatus === RECEIVED_SETTLING_STATUS;

const isSuccessfulFinalState = (status?: string | null) =>
  status === 'Completed' || status === 'PayoutCompleted';

const parseTransactionSignature = (signature?: string | null) => {
  if (!signature) {
    return { status: null, displayStatus: null };
  }

  const separatorIndex = signature.indexOf('|');
  if (separatorIndex < 0) {
    return { status: signature, displayStatus: signature };
  }

  return {
    status: signature.slice(0, separatorIndex),
    displayStatus: signature.slice(separatorIndex + 1),
  };
};

const compactLabel = (value: string, max = 28) => {
  const normalized = value.trim();
  if (normalized.length <= max) {
    return normalized;
  }

  const start = Math.max(0, max - 10);
  return `${normalized.slice(0, start)}...${normalized.slice(-7)}`;
};

const buildDashboardTransactionSignature = (transactions: Transaction[]) => JSON.stringify(
  transactions.map((transaction) => ({
    id: transaction.id,
    status: transaction.status,
    displayStatus: transaction.displayStatus,
    amount: transaction.amount,
    currency: transaction.currency,
    fiatAmount: transaction.fiatAmount,
    fiatCurrency: transaction.fiatCurrency,
    timestamp: transaction.timestamp,
    updatedAt: transaction.updatedAt,
  })),
);

const buildDashboardNotificationSignature = (notifications: AppNotification[]) => JSON.stringify(
  notifications.map((notification) => ({
    id: notification.id,
    type: notification.type,
    severity: notification.severity,
    createdAt: notification.createdAt,
    readAt: notification.readAt,
    title: notification.title,
    transactionId: notification.transactionId,
  })),
);

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [activityItems, setActivityItems] = useState<Array<{
    id: string;
    title: string;
    description: string;
    occurredAt: string;
    variant: 'success' | 'info';
  }>>([]);
  const [recentNotifications, setRecentNotifications] = useState<AppNotification[]>([]);

  const [globalStats, setGlobalStats] = useState<GlobalStats | null>(null);
  const [opsSummary, setOpsSummary] = useState<OpsAlertSummary | null>(null);
  const [userStats, setUserStats] = useState<UserStatsResponse | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [showNotificationsPanel, setShowNotificationsPanel] = useState<boolean>(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<string | null>(null);
  const [liveToast, setLiveToast] = useState<{ title: string; message: string } | null>(null);
  const [notificationPermission, setNotificationPermission] = useState<NotificationPermission | 'unsupported'>(() => getBrowserAlertPermission());
  const [paymentToneEnabled, setPaymentToneState] = useState<boolean>(() => getPaymentToneEnabled());
  const dropdownRef = useRef<HTMLDivElement>(null);
  const previousStatusesRef = useRef<Map<number, string>>(new Map());
  const transactionsSignatureRef = useRef<string>('');
  const notificationsSignatureRef = useRef<string>('');
  const globalStatsSignatureRef = useRef<string>('null');
  const opsSummarySignatureRef = useRef<string>('null');
  const userStatsSignatureRef = useRef<string>('null');

  const userRole = getCurrentUserRole();
  const userId = getCurrentUserId();

  useEffect(() => {
    if (userRole === 'Agent') {
      // Agent users can only see their own data
      setSelectedUserId(userId || '');
    } else if (userRole === 'Admin') {
      // Admin users see global stats
      setSelectedUserId('global');
    } else if (userRole === 'SuperAgent') {
      // SuperAgent users see their own data immediately
      setSelectedUserId(userId || '');
    }
  }, [userRole, userId]);

  useEffect(() => {
    // This effect handles user selection after users are loaded
    if (userRole === 'Admin' && users.length > 0) {
      // Admin users can select different users after they're loaded
      // Keep the existing logic for user selection
    }
  }, [users, userRole]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const usersData = await dashboardService.getUsers();
      setUsers(usersData);
    } catch (err) {
      setError('Failed to fetch users');
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Only fetch users for Admin and SuperAgent roles
    if (userRole === 'Admin' || userRole === 'SuperAgent') {
      void fetchUsers();
    }
  }, [fetchUsers, userRole]);

  const fetchTransactions = useCallback(async (silent: boolean = false) => {
    try {
      const transactionsData = await dashboardService.getTransactions(5);
      const sortedTransactions = sortByNewest(transactionsData, {
        dateKeys: ['timestamp', 'createdAt', 'updatedAt'],
        numericFallbackKeys: ['id'],
      }).slice(0, 5);

      const previousStatuses = previousStatusesRef.current;
      const nextStatuses = new Map<number, string>();
      let changedTransaction: Transaction | undefined;

      sortedTransactions.forEach((transaction) => {
        const signature = `${transaction.status}|${transaction.displayStatus || transaction.status}`;
        nextStatuses.set(transaction.id, signature);

        const previousSignature = previousStatuses.get(transaction.id);
        if (!changedTransaction && previousSignature && previousSignature !== signature) {
          changedTransaction = transaction;
        }
      });

      previousStatusesRef.current = nextStatuses;

      const nextSignature = buildDashboardTransactionSignature(sortedTransactions);
      const transactionsChanged = nextSignature !== transactionsSignatureRef.current;
      transactionsSignatureRef.current = nextSignature;

      if (!silent || transactionsChanged) {
        setTransactions(sortedTransactions);
        setLastRefreshedAt(new Date().toISOString());
      }

      if (changedTransaction !== undefined) {
        const changed = changedTransaction;
        const previousSignature = previousStatuses.get(changed.id);
        const previousState = parseTransactionSignature(previousSignature);
        const nextLabel = changed.displayStatus || changed.status;
        const paymentWasAlreadyReceived = isPaymentReceivedState(previousState.status, previousState.displayStatus);
        const paymentReceivedNow = isPaymentReceivedState(changed.status, changed.displayStatus);
        const paymentJustReceived = paymentReceivedNow && !paymentWasAlreadyReceived;
        const paymentJustCompleted = isSuccessfulFinalState(changed.status);
        const occurredAt = new Date().toISOString();
        const title = paymentJustReceived
          ? 'Payment received'
          : paymentJustCompleted
            ? 'Payment complete'
            : 'Transaction updated';
        const message = paymentJustReceived
          ? `We received payment for transaction #${changed.id} and are settling it now.`
          : `Transaction #${changed.id} is now ${nextLabel}.`;
        const variant = paymentJustReceived || paymentJustCompleted ? 'success' as const : 'info' as const;

        setLiveToast({
          title,
          message,
        });
        setActivityItems((current) => [
          {
            id: `${changed.id}-${changed.status}-${occurredAt}`,
            title,
            description: message,
            occurredAt,
            variant,
          },
          ...current,
        ].slice(0, 8));

        if (paymentJustReceived || (paymentJustCompleted && !paymentWasAlreadyReceived)) {
          sendBrowserAlert(title, message, `dashboard-transaction-${changed.id}-${changed.status}`);
          void playPaymentTone();
        }
      }
    } catch (err) {
      console.error('Error fetching transactions:', err);
      // Don't set error state for transactions as it's not critical
    }
  }, []);

  const fetchRecentNotifications = useCallback(async (silent: boolean = false) => {
    try {
      const notifications = sortByNewest(
        await appNotificationService.getNotifications(6, false),
        {
          dateKeys: ['createdAt'],
          numericFallbackKeys: ['id'],
        },
      );
      const nextSignature = buildDashboardNotificationSignature(notifications);
      const notificationsChanged = nextSignature !== notificationsSignatureRef.current;
      notificationsSignatureRef.current = nextSignature;

      if (!silent || notificationsChanged) {
        setRecentNotifications(notifications);
      }
    } catch (err) {
      console.error('Error fetching recent notifications:', err);
    }
  }, []);

  const fetchDashboardData = useCallback(async (silent: boolean = false) => {
    try {
      if (!silent) {
        setLoading(true);
        setError(null);
      }

      // Only fetch stats for Admin and SuperAgent roles
      if (userRole !== 'Agent') {
        if (selectedUserId === 'global') {
          // Admin gets global stats
          const [globalData, opsData] = await Promise.all([
            dashboardService.getGlobalStats(),
            dashboardService.getOpsSummary(),
          ]);
          const nextGlobalSignature = JSON.stringify(globalData);
          if (!silent || nextGlobalSignature !== globalStatsSignatureRef.current) {
            globalStatsSignatureRef.current = nextGlobalSignature;
            setGlobalStats(globalData);
          }

          const nextOpsSignature = JSON.stringify(opsData);
          if (!silent || nextOpsSignature !== opsSummarySignatureRef.current) {
            opsSummarySignatureRef.current = nextOpsSignature;
            setOpsSummary(opsData);
          }

          if (!silent || userStatsSignatureRef.current !== 'null') {
            userStatsSignatureRef.current = 'null';
            setUserStats(null);
          }
        } else {
          // Get user-specific stats
          const [userData, opsData] = await Promise.all([
            dashboardService.getUserStats(parseInt(selectedUserId)),
            dashboardService.getOpsSummary(),
          ]);
          const nextUserSignature = JSON.stringify(userData);
          if (!silent || nextUserSignature !== userStatsSignatureRef.current) {
            userStatsSignatureRef.current = nextUserSignature;
            setUserStats(userData);
          }

          const nextOpsSignature = JSON.stringify(opsData);
          if (!silent || nextOpsSignature !== opsSummarySignatureRef.current) {
            opsSummarySignatureRef.current = nextOpsSignature;
            setOpsSummary(opsData);
          }

          if (!silent || globalStatsSignatureRef.current !== 'null') {
            globalStatsSignatureRef.current = 'null';
            setGlobalStats(null);
          }
        }
      } else {
        // Agent role - clear stats and only fetch transactions
        if (!silent || globalStatsSignatureRef.current !== 'null') {
          globalStatsSignatureRef.current = 'null';
          setGlobalStats(null);
        }

        if (!silent || userStatsSignatureRef.current !== 'null') {
          userStatsSignatureRef.current = 'null';
          setUserStats(null);
        }

        const opsData = await dashboardService.getOpsSummary();
        const nextOpsSignature = JSON.stringify(opsData);
        if (!silent || nextOpsSignature !== opsSummarySignatureRef.current) {
          opsSummarySignatureRef.current = nextOpsSignature;
          setOpsSummary(opsData);
        }
      }

      // Fetch transactions for all roles
      await Promise.all([
        fetchTransactions(silent),
        fetchRecentNotifications(silent),
      ]);
    } catch (err) {
      if (!silent) {
        setError('Failed to fetch dashboard data');
      }
      console.error('Error fetching dashboard data:', err);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [fetchRecentNotifications, fetchTransactions, selectedUserId, userRole]);

  useEffect(() => {
    if (selectedUserId) {
      void fetchDashboardData();
    }
  }, [fetchDashboardData, selectedUserId]);

  useEffect(() => {
    if (!selectedUserId) {
      return;
    }

    const handle = window.setInterval(() => {
      void fetchDashboardData(true);
    }, DASHBOARD_POLL_INTERVAL_MS);

    return () => window.clearInterval(handle);
  }, [fetchDashboardData, selectedUserId]);

  useEffect(() => {
    const handleNotificationsChanged = () => {
      void fetchRecentNotifications(true);
    };

    window.addEventListener(APP_NOTIFICATIONS_CHANGED_EVENT, handleNotificationsChanged);
    return () => window.removeEventListener(APP_NOTIFICATIONS_CHANGED_EVENT, handleNotificationsChanged);
  }, [fetchRecentNotifications]);

  const getFilteredUsers = (): User[] => {
    let filteredUsers: User[] = [];
    
    if (userRole === 'Admin') {
      filteredUsers = users; // Admin sees all users
    } else if (userRole === 'SuperAgent') {
      // SuperAgent sees self and underlying users (Agents)
      filteredUsers = users.filter(user => 
        user.id === parseInt(userId || '0') || 
        (user.role === 'Agent' && user.id !== parseInt(userId || '0'))
      );
    } else {
      // Agent sees only self
      filteredUsers = users.filter(user => user.id === parseInt(userId || '0'));
    }

    // Apply search filter
    if (searchTerm.trim()) {
      filteredUsers = filteredUsers.filter(user => 
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filteredUsers;
  };

  const getSelectedUserDisplay = (): string => {
    if (selectedUserId === 'global') {
      return 'Global overview';
    }
    
    const user = users.find(u => u.id === parseInt(selectedUserId));
    if (user) {
      return `${user.name} (${user.role})`;
    }
    
    return 'Choose a view';
  };

  const formatDate = (timestamp: string): { date: string; time: string } => {
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) {
        return { date: 'Invalid Date', time: '' };
      }
      return {
        date: date.toLocaleDateString(),
        time: date.toLocaleTimeString()
      };
    } catch (error) {
      console.error('Error formatting date:', error);
      return { date: 'Invalid Date', time: '' };
    }
  };

  const handleUserSelect = (userId: string) => {
    setSelectedUserId(userId);
    setSearchTerm('');
    setShowDropdown(false);
  };

  const handleEnableBrowserAlerts = async () => {
    const permission = await requestBrowserAlertPermission();
    setNotificationPermission(permission);
  };

  const handleTogglePaymentTone = () => {
    const nextValue = !paymentToneEnabled;
    setPaymentToneState(nextValue);
    setPaymentToneEnabled(nextValue);
  };

  const getStatusBadgeClass = (status: string) => {
    if (status === 'Completed' || status === 'PayoutCompleted') {
      return 'bg-success';
    }

    if (status === 'Issued') {
      return 'bg-primary';
    }

    if (status === 'Pending') {
      return 'bg-warning';
    }

    if (status === 'Expired') {
      return 'bg-secondary';
    }

    return 'bg-danger';
  };

  const formatSettledVolume = (value: number | undefined) => (
    new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 0,
    }).format(Math.trunc(value || 0))
  );

  const renderSettledVolume = (value: number | undefined) => (
    <span className="dashboard-token-value">
      <span>{formatSettledVolume(value)}</span>
      <span className="dashboard-token-badge" aria-label="Settlement token" title="Settlement token">S</span>
    </span>
  );

  const handleOpenNotification = async (notification: AppNotification) => {
    try {
      if (!notification.readAt && notification.transactionId) {
        await appNotificationService.markTransactionNotificationsAsRead(notification.transactionId);
      } else if (!notification.readAt) {
        await appNotificationService.markAsRead(notification.id);
      }
    } catch (error) {
      console.error('Failed to mark dashboard notification as read', error);
    }

    const safeRoute = resolveSafeOperatorRoute(notification.linkUrl);
    const href = safeRoute || (notification.transactionId
      ? `/transactions?search=${notification.transactionId}&openTransactionId=${notification.transactionId}`
      : '/transactions');

    navigate(href);
  };

  const formatAgeMinutes = (minutes?: number | null) => {
    if (minutes === null || minutes === undefined) {
      return 'No aged cases';
    }

    if (minutes < 60) {
      return `${minutes} min`;
    }

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  };

  const renderOpsCard = (title: string, bucket: OpsAlertBucket | undefined, accent: string, href: string, helperText: string) => (
    <Card className="dashboard-ops-card" style={{ ['--ops-accent' as string]: accent }}>
      <Card.Body>
        <div className="dashboard-ops-card__eyebrow">{title}</div>
        <div className="dashboard-ops-card__value">{bucket?.count || 0}</div>
        <div className="dashboard-ops-card__meta">Oldest age: {formatAgeMinutes(bucket?.oldestAgeMinutes)}</div>
        <div className="dashboard-ops-card__helper">
          {bucket?.oldestReference
            ? `Oldest ref: ${bucket.oldestReference}${bucket.oldestMerchantUserId ? ` | Player: ${bucket.oldestMerchantUserId}` : ''}${bucket.oldestCaseOwnerName ? ` | Owner: ${bucket.oldestCaseOwnerName}` : ''}`
            : helperText}
        </div>
        <div className="dashboard-ops-card__footer">
          <Button variant="outline-light" size="sm" onClick={() => navigate(href)}>
            Open queue
          </Button>
        </div>
      </Card.Body>
    </Card>
  );

  const renderCompactOpsCard = (title: string, bucket: OpsAlertBucket | undefined, accent: string, href: string) => (
    <button
      type="button"
      className="dashboard-ops-compact-card"
      style={{ ['--ops-accent' as string]: accent } as React.CSSProperties}
      onClick={() => navigate(href)}
    >
      <div className="dashboard-ops-compact-card__label">{title}</div>
      <div className="dashboard-ops-compact-card__value">{bucket?.count || 0}</div>
      <div className="dashboard-ops-compact-card__meta">
        {bucket?.count ? `Oldest ${formatAgeMinutes(bucket?.oldestAgeMinutes)}` : 'No items waiting'}
      </div>
      <div className="dashboard-ops-compact-card__action">Open queue</div>
    </button>
  );

  const renderRecentTransactionItem = (transaction: Transaction) => {
    const transactionUser = users.find((user) => user.id === transaction.userId);
    const { date, time } = formatDate(transaction.timestamp);
    const primaryAmount = transaction.fiatAmount
      ? `${transaction.fiatAmount} ${transaction.fiatCurrency}`
      : `${transaction.amount} ${transaction.currency}`;
    const secondaryAmount = transaction.fiatAmount
      ? `${transaction.amount} ${transaction.currency}`
      : transaction.paymentMethod || 'Crypto payment';

    return (
      <button
        key={transaction.id}
        type="button"
        className="dashboard-payment-item"
        onClick={() => navigate(`/transactions?search=${transaction.id}&openTransactionId=${transaction.id}`)}
      >
        <div className="dashboard-payment-item__top">
          <div className="dashboard-payment-item__identity">
            <div className="dashboard-payment-item__title">Transaction #{transaction.id}</div>
            <div className="dashboard-payment-item__meta">
              {transactionUser ? transactionUser.name : transaction.userName || `User ${transaction.userId}`}
            </div>
          </div>
          <span className={`badge ${getStatusBadgeClass(transaction.status)}`}>
            {transaction.displayStatus || transaction.status}
          </span>
        </div>
        <div className="dashboard-payment-item__amount">{primaryAmount}</div>
        <div className="dashboard-payment-item__submeta">
          <span>{secondaryAmount}</span>
          <span>{date}{time ? `, ${time}` : ''}</span>
        </div>
      </button>
    );
  };

  const myAssignedQueueHref = userId
    ? `/transactions?caseOwnerUserId=${userId}&caseStatus=Unresolved`
    : '/transactions?caseStatus=Unresolved';

  const totalIssued = globalStats?.totalIssued ?? userStats?.totalIssued ?? 0;
  const totalCompleted = globalStats?.totalCompleted ?? userStats?.totalCompleted ?? 0;
  const totalPending = globalStats?.totalPending ?? userStats?.totalPending ?? 0;
  const totalFailed = globalStats?.totalFailed ?? userStats?.totalFailed ?? 0;
  const totalVolume = globalStats?.totalVolume ?? userStats?.totalVolume ?? 0;
  const subUsersCount = userStats?.subUsers?.length ?? 0;
  const filteredUsers = getFilteredUsers();
  const activeScopeLabel = selectedUserId ? getSelectedUserDisplay() : userRole === 'Agent' ? 'Your account' : 'Select a user';
  const activeScopeDisplayLabel = compactLabel(activeScopeLabel, 28);
  const roleLabel = getRoleDisplayLabel(userRole);
  const alertingLabel = [
    browserAlertsSupported() ? (notificationPermission === 'granted' ? 'browser alerts ready' : 'browser alerts off') : 'browser alerts unavailable',
    paymentToneEnabled ? 'payment tone on' : 'payment tone off',
  ].join(' | ');
  const queuePressure = (
    (opsSummary?.openExceptionsOlderThanThreshold.count ?? 0)
    + (opsSummary?.webhookRetriesOlderThanThreshold.count ?? 0)
    + (opsSummary?.unresolvedUnderpayments.count ?? 0)
  );
  const metricCards = [
    { key: 'issued', label: 'Requests created', value: totalIssued, accent: '#0088cc', note: 'Every payment request in the current view.' },
    { key: 'completed', label: 'Completed', value: totalCompleted, accent: '#eeeeee', note: 'Payments the system already received.' },
    { key: 'pending', label: 'Waiting', value: totalPending, accent: '#979797', note: 'Payments still moving through the flow.' },
    { key: 'failed', label: 'Needs attention', value: totalFailed, accent: '#666666', note: 'Payments that stopped or need review.' },
    { key: 'volume', label: 'Settled volume', value: renderSettledVolume(totalVolume), accent: '#0088cc', note: 'Settlement volume already completed in this scope.' },
    { key: 'sub-users', label: 'Visible accounts', value: subUsersCount, accent: '#979797', note: 'Accounts currently inside your workspace.' },
  ];
  const priorityQueueCards = [
    {
      key: 'exceptions',
      title: 'Open exceptions',
      bucket: opsSummary?.openExceptionsOlderThanThreshold,
      accent: '#979797',
      href: '/transactions?exceptionType=any&caseStatus=Unresolved',
      helperText: `Exceptions older than ${opsSummary?.openExceptionsOlderThanThreshold.thresholdMinutes || 30} minutes will appear here.`,
    },
    {
      key: 'webhooks',
      title: 'Webhook retries',
      bucket: opsSummary?.webhookRetriesOlderThanThreshold,
      accent: '#666666',
      href: '/transactions?exceptionType=webhook_retrying',
      helperText: `Webhook retries older than ${opsSummary?.webhookRetriesOlderThanThreshold.thresholdMinutes || 15} minutes will appear here.`,
    },
    {
      key: 'underpayments',
      title: 'Unresolved underpayments',
      bucket: opsSummary?.unresolvedUnderpayments,
      accent: '#0088cc',
      href: '/transactions?exceptionType=underpaid&caseStatus=Unresolved',
      helperText: 'Underpayments that still need a support decision stay here until resolved.',
    },
  ];
  const secondaryQueueCards = [
    {
      key: 'overdue',
      title: 'Overdue cases',
      bucket: opsSummary?.overdueCases,
      accent: '#979797',
      href: '/transactions?workflowState=overdue&caseStatus=Unresolved',
    },
    {
      key: 'escalated',
      title: 'Escalated',
      bucket: opsSummary?.escalatedCases,
      accent: '#666666',
      href: '/transactions?workflowState=escalated',
    },
    {
      key: 'assigned',
      title: 'Assigned to me',
      bucket: opsSummary?.myAssignedCases,
      accent: '#666666',
      href: myAssignedQueueHref,
    },
    {
      key: 'unassigned',
      title: 'Unassigned',
      bucket: opsSummary?.unassignedCases,
      accent: '#979797',
      href: '/transactions?caseOwnerUserId=0&caseStatus=Unresolved',
    },
    {
      key: 'reminder-due',
      title: 'Reminder due',
      bucket: opsSummary?.reminderDueCases,
      accent: '#0088cc',
      href: '/transactions?workflowState=reminder_due&caseStatus=Unresolved',
    },
    {
      key: 'escalation-due',
      title: 'Escalation due',
      bucket: opsSummary?.escalationDueCases,
      accent: '#666666',
      href: '/transactions?workflowState=escalation_due&caseStatus=Unresolved',
    },
  ];
  const subUsersPreview = userStats?.subUsers
    ? [...userStats.subUsers].sort((left, right) => right.stats.totalVolume - left.stats.totalVolume).slice(0, 4)
    : [];
  const hasMoreSubUsers = (userStats?.subUsers?.length ?? 0) > subUsersPreview.length;
  const recentTransactionsTitle = userRole === 'Admin'
    ? 'Recent payments across the platform'
    : userRole === 'SuperAgent'
      ? 'Recent payments across your portfolio'
      : 'Recent payments in your account';
  const dashboardHeaderCopy = userRole === 'Admin'
    ? 'Track platform flow, queue pressure, and the accounts that need attention.'
    : userRole === 'SuperAgent'
      ? `Watch your portfolio, your ${SUB_MERCHANT_LABEL.toLowerCase()}s, and the follow-up work that still needs action.`
      : 'Stay on top of your incoming payments, settlement progress, and exceptions.';

  return (
    <div className="dashboard-container dashboard-page operator-page">
      {/* Top Header */}

      <Container fluid className="p-4 dashboard-page__container operator-page__container">
        <ToastContainer position="top-end" className="p-3">
          <Toast bg="primary" onClose={() => setLiveToast(null)} show={!!liveToast} delay={6000} autohide>
            <Toast.Header closeButton>
              <strong className="me-auto">{liveToast?.title}</strong>
            </Toast.Header>
            <Toast.Body className="text-white">{liveToast?.message}</Toast.Body>
          </Toast>
        </ToastContainer>

        <Row className="mb-4">
          <Col>
            <InternalPageHeader
              eyebrow="Operations overview"
              title="Dashboard"
              description={dashboardHeaderCopy}
              meta={[
                { label: 'Scope', value: activeScopeDisplayLabel },
                { label: 'Role', value: roleLabel },
                {
                  label: 'Refresh',
                  value: lastRefreshedAt ? `Last checked ${new Date(lastRefreshedAt).toLocaleTimeString()}` : 'Every 15 seconds',
                },
                { label: 'Alerts', value: alertingLabel },
                { label: 'Needs review', value: queuePressure },
                { label: 'Settled volume', value: renderSettledVolume(totalVolume) },
              ]}
            />
          </Col>
        </Row>

        <div className={`dashboard-page__control-grid mb-4 ${userRole === 'Agent' ? 'dashboard-page__control-grid--agent' : ''}`}>
          {(userRole === 'Admin' || userRole === 'SuperAgent') && (
            <Card className="dashboard-filter-card dashboard-page__scope-card">
              <Card.Body className="dashboard-filter-card__body">
                <div className="dashboard-filter-card__header">
                  <div>
                    <div className="dashboard-filter-card__eyebrow">Workspace scope</div>
                    <h5 className="dashboard-filter-card__title">Open the account view you want right now.</h5>
                    <p className="dashboard-filter-card__copy">
                      Search once, then keep the rest of the board focused on that account or stay global.
                    </p>
                  </div>
                  <div className="dashboard-filter-meta">
                    Refreshes every 15 seconds
                    {lastRefreshedAt ? ` | Last checked ${new Date(lastRefreshedAt).toLocaleTimeString()}` : ''}
                  </div>
                </div>
                <Form.Group>
                  <Form.Label className="fw-semibold">Account search</Form.Label>
                  <div className={`operator-search-surface ${showDropdown ? 'operator-search-surface--open' : ''}`} ref={dropdownRef}>
                    <Form.Control
                      className="operator-control"
                      type="text"
                      placeholder={loading ? 'Loading accounts...' : 'Search accounts by name or email'}
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setShowDropdown(true);
                      }}
                      onFocus={() => setShowDropdown(true)}
                      disabled={loading}
                    />
                    <button
                      type="button"
                      className="operator-search-toggle"
                      aria-label={showDropdown ? 'Hide account list' : 'Show account list'}
                      onClick={() => setShowDropdown(!showDropdown)}
                    >
                      <span className="operator-toggle-indicator">{showDropdown ? 'Hide' : 'Show'}</span>
                    </button>

                    {showDropdown && !loading && (
                      <div className="operator-search-menu">
                        {userRole === 'Admin' && (
                          <button
                            type="button"
                            className={`operator-search-option ${selectedUserId === 'global' ? 'operator-search-option--active' : ''}`}
                            onClick={() => handleUserSelect('global')}
                          >
                            Global overview
                            <span className="operator-search-option__meta">
                              See the full platform without narrowing to one account.
                            </span>
                          </button>
                        )}
                        {filteredUsers.length > 0 ? (
                          filteredUsers.map((user) => (
                            <button
                              key={user.id}
                              type="button"
                              className={`operator-search-option ${selectedUserId === user.id.toString() ? 'operator-search-option--active' : ''}`}
                              onClick={() => handleUserSelect(user.id.toString())}
                            >
                              {user.name} ({getRoleDisplayLabel(user.role)})
                              <span className="operator-search-option__meta">{user.email}</span>
                            </button>
                          ))
                        ) : (
                          <div className="dashboard-filter-card__empty">No accounts match this search yet.</div>
                        )}
                      </div>
                    )}
                  </div>

                  {selectedUserId && (
                    <div className="dashboard-selected-scope">
                      <small>Current workspace</small>
                      <div>
                        <strong>{activeScopeDisplayLabel}</strong>
                      </div>
                    </div>
                  )}
                </Form.Group>
              </Card.Body>
            </Card>
          )}
          <Card className="dashboard-filter-card dashboard-page__alerts-card">
            <Card.Body className="dashboard-filter-card__body">
              <div className="dashboard-filter-card__header">
                <div>
                  <div className="dashboard-filter-card__eyebrow">Alerts</div>
                  <h5 className="dashboard-filter-card__title">Keep live payment signals easy to notice.</h5>
                  <p className="dashboard-filter-card__copy">
                    Choose browser alerts, keep the payment tone on, and leave the rest of the board quiet.
                  </p>
                </div>
              </div>
              <div className="dashboard-filter-card__actions">
                {browserAlertsSupported() ? (
                  notificationPermission !== 'granted' ? (
                    <Button variant="outline-info" size="sm" onClick={() => void handleEnableBrowserAlerts()}>
                      Turn on browser alerts
                    </Button>
                  ) : (
                    <div className="dashboard-filter-card__status-pill dashboard-filter-card__status-pill--active">
                      Browser alerts are on
                    </div>
                  )
                ) : (
                  <div className="dashboard-filter-card__status-pill">
                    Browser alerts are not available in this browser
                  </div>
                )}
                <Button
                  variant={paymentToneEnabled ? 'outline-success' : 'outline-secondary'}
                  size="sm"
                  onClick={handleTogglePaymentTone}
                >
                  {paymentToneEnabled ? 'Payment tone on' : 'Payment tone off'}
                </Button>
              </div>
              <div className="dashboard-selected-scope dashboard-selected-scope--support">
                <small>Alert status</small>
                <div>
                  <strong>{alertingLabel}</strong>
                </div>
              </div>
            </Card.Body>
          </Card>
        </div>

        {/* Transaction Statistics Cards */}
        {loading ? (
          <Row className="mb-4">
            <Col className="text-center">
              <Spinner animation="border" className="app-spinner">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </Col>
          </Row>
        ) : error ? (
          <Row className="mb-4">
            <Col>
              <Alert variant="danger">
                {error}
                <Button variant="outline-light" className="ms-2" onClick={() => void fetchDashboardData()}>
                  Retry
                </Button>
              </Alert>
            </Col>
          </Row>
        ) : (
          <div className="dashboard-page__metric-grid mb-4">
            {metricCards.map((metric) => (
              <Card
                key={metric.key}
                className="dashboard-metric-card"
                style={{ ['--metric-accent' as string]: metric.accent }}
              >
                <Card.Body>
                  <div className="dashboard-metric-label">{metric.label}</div>
                  <div className="dashboard-metric-value">{metric.value}</div>
                  <div className="dashboard-metric-note">{metric.note}</div>
                </Card.Body>
              </Card>
            ))}
          </div>
        )}

        <Row className="mb-4">
          <Col>
            <Card className="app-surface-card dashboard-section-card">
              <Card.Header>
                <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
                  <h5 className="mb-0">Ops Priority Queue</h5>
                  <div className="operator-muted-text">
                    {opsSummary?.generatedAtUtc
                      ? `Updated ${new Date(opsSummary.generatedAtUtc).toLocaleTimeString()}`
                      : 'Live SLA checks refresh every 15 seconds'}
                  </div>
                </div>
              </Card.Header>
              <Card.Body>
                <div className="dashboard-ops-layout">
                  <Row className="g-3">
                    {priorityQueueCards.map((card) => (
                      <Col lg={4} key={card.key}>
                        {renderOpsCard(card.title, card.bucket, card.accent, card.href, card.helperText)}
                      </Col>
                    ))}
                  </Row>
                  <div className="dashboard-ops-secondary">
                    <div className="dashboard-ops-secondary__intro">
                      Smaller queues stay here so you can still spot overdue follow-ups without turning the dashboard into a wall of cards.
                    </div>
                    <div className="dashboard-ops-secondary__grid">
                      {secondaryQueueCards.map((card) => renderCompactOpsCard(card.title, card.bucket, card.accent, card.href))}
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {userRole !== 'Agent' && subUsersPreview.length > 0 && (
          <Row className="mb-4">
            <Col>
              <Card className="app-surface-card dashboard-section-card">
                <Card.Header>
                  <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
                    <div>
                      <h5 className="mb-0">Top account snapshots</h5>
                      <div className="dashboard-section-copy">The busiest accounts in the current view, ranked by settled volume.</div>
                    </div>
                    {hasMoreSubUsers && (
                      <Button variant="outline-light" size="sm" onClick={() => navigate('/users')}>
                        Open all accounts
                      </Button>
                    )}
                  </div>
                </Card.Header>
                <Card.Body>
                  <div className="dashboard-subusers-grid">
                    {subUsersPreview.map((subUser) => {
                      const subUserData = users.find(user => user.id === subUser.userId);
                      return (
                        <Card className="dashboard-subuser-card" key={subUser.userId}>
                            <Card.Header>
                              <div className="dashboard-subuser-card__name">
                                {subUserData ? subUserData.name : `User ${subUser.userId}`}
                              </div>
                              <div className="dashboard-subuser-card__role">
                                {subUserData ? `(${subUserData.role})` : `ID: ${subUser.userId}`}
                              </div>
                            </Card.Header>
                          <Card.Body>
                            <div className="dashboard-subuser-stats">
                              <div className="dashboard-subuser-stat">
                                <div className="dashboard-subuser-stat__label">Issued</div>
                                <div className="dashboard-subuser-stat__value">{subUser.stats.totalIssued}</div>
                              </div>
                              <div className="dashboard-subuser-stat">
                                <div className="dashboard-subuser-stat__label">Completed</div>
                                <div className="dashboard-subuser-stat__value">{subUser.stats.totalCompleted}</div>
                              </div>
                              <div className="dashboard-subuser-stat">
                                <div className="dashboard-subuser-stat__label">Pending</div>
                                <div className="dashboard-subuser-stat__value">{subUser.stats.totalPending}</div>
                              </div>
                              <div className="dashboard-subuser-stat">
                                <div className="dashboard-subuser-stat__label">Needs review</div>
                                <div className="dashboard-subuser-stat__value">{subUser.stats.totalFailed}</div>
                              </div>
                              <div className="dashboard-subuser-stat" style={{ gridColumn: '1 / -1' }}>
                                <div className="dashboard-subuser-stat__label">Settled Volume</div>
                                <div className="dashboard-subuser-stat__value dashboard-subuser-stat__value--accent">{formatSettledVolume(subUser.stats.totalVolume)}</div>
                              </div>
                            </div>
                          </Card.Body>
                        </Card>
                      );
                    })}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        <Row className="mb-4 dashboard-page__stream-grid">
          <Col lg={7}>
            <Card className="app-surface-card dashboard-section-card h-100">
              <Card.Header>
                <h5 className="mb-0">Recent Activity</h5>
              </Card.Header>
              <Card.Body>
                {activityItems.length > 0 ? (
                  <div className="dashboard-list">
                    {activityItems.map((item) => (
                      <div
                        key={item.id}
                        className={`dashboard-list-item ${item.variant === 'success' ? 'dashboard-list-item--success' : ''}`}
                      >
                        <div className="dashboard-list-item__title">{item.title}</div>
                        <div className="dashboard-list-item__body">{item.description}</div>
                        <div className="dashboard-list-item__time">
                          {new Date(item.occurredAt).toLocaleString()}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="dashboard-list-empty">
                    Live payment and transaction updates will appear here as they happen.
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
          <Col lg={5} className="mt-4 mt-lg-0">
            <Card className="app-surface-card dashboard-section-card h-100">
              <Card.Header>
                <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
                  <h5 className="mb-0">Recent Notifications</h5>
                  <div className="d-flex flex-wrap gap-2">
                    <Button
                      variant="outline-light"
                      size="sm"
                      onClick={() => void fetchRecentNotifications()}
                    >
                      Refresh
                    </Button>
                    <Button
                      variant="outline-light"
                      size="sm"
                      onClick={() => setShowNotificationsPanel((previous) => !previous)}
                    >
                      {showNotificationsPanel ? 'Hide' : 'View'}
                    </Button>
                  </div>
                </div>
              </Card.Header>
              <Card.Body>
                {showNotificationsPanel && recentNotifications.length > 0 ? (
                  <div className="dashboard-list">
                    {recentNotifications.map((notification) => (
                      <button
                        key={notification.id}
                        type="button"
                        onClick={() => void handleOpenNotification(notification)}
                        className={`dashboard-notification-button ${notification.readAt ? '' : 'dashboard-notification-button--unread'}`}
                      >
                        <div className="d-flex justify-content-between align-items-start gap-3">
                          <div className="dashboard-notification-button__body">
                            <div className="dashboard-list-item__title">{notification.title}</div>
                            <div className="dashboard-list-item__body">
                              {notification.message}
                            </div>
                            <div className="dashboard-list-item__time">
                              {new Date(notification.createdAt).toLocaleString()}
                            </div>
                          </div>
                          {!notification.readAt && (
                            <span className="badge bg-danger">New</span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                ) : showNotificationsPanel ? (
                  <div className="dashboard-list-empty">
                    Payment, invoice, and ops notifications will appear here as the platform detects them.
                  </div>
                ) : (
                  <div className="dashboard-list-empty dashboard-list-empty--summary">
                    {recentNotifications.length > 0
                      ? `${recentNotifications.length} recent notification${recentNotifications.length === 1 ? '' : 's'} ready to review. Open this list only when you need the details.`
                      : 'Notifications stay tucked away until the system has something worth your attention.'}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="mb-4">
          <Col>
            <Card className="app-surface-card dashboard-section-card">
              <Card.Header>
                <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
                  <div>
                    <h5 className="mb-0">Recent payments</h5>
                    <div className="dashboard-section-copy">{recentTransactionsTitle}</div>
                  </div>
                  <Button
                    variant="outline-light"
                    size="sm"
                    onClick={() => navigate('/transactions')}
                  >
                    Open full review workspace
                  </Button>
                </div>
              </Card.Header>
              <Card.Body>
                {transactions.length > 0 ? (
                  <div className="dashboard-payments-list">
                    {transactions.map((transaction) => renderRecentTransactionItem(transaction))}
                  </div>
                ) : (
                  <div className="dashboard-list-empty">
                    New payments will appear here as soon as they are created or updated.
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <div className="dashboard-footer">
          <small>Last updated: {lastRefreshedAt ? new Date(lastRefreshedAt).toLocaleTimeString() : 'Waiting for first sync'}</small>
        </div>
      </Container>
    </div>
  );
};

export default Dashboard;

